import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

// ── Types ──

interface Period {
  name: string;
  start: string;
  end: string;
  subject: string;
}

interface SchoolInfo {
  name: string;
  district: string;
  timezone: string;
  address: string;
}

interface Schedule {
  periods: Period[];
  notes: string;
}

interface AiContext {
  teaching_style: string;
  subjects: string[];
  grade_focus: string;
  notes: string;
}

interface PublicConfig {
  school?: SchoolInfo;
  schedule?: Schedule;
  ai_context?: AiContext;
}

interface Classroom {
  id: string;
  name: string;
  public_name: string | null;
  grade_level: string | null;
  notes: string | null;
  storybooks_enabled: boolean;
  public_config: PublicConfig;
}

const TABS = ['General', 'School Info', 'Schedule', 'AI Context'] as const;
type Tab = typeof TABS[number];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Phoenix',
  'America/Indiana/Indianapolis',
];

const EMPTY_PERIOD: Period = { name: '', start: '', end: '', subject: '' };

// ── Main Component ──

interface ClassroomSummary {
  id: string;
  name: string;
  grade_level: string | null;
  public_config: PublicConfig;
}

export default function ClassroomSettings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token || '';

  // Classroom picker state (when no id in URL)
  const [classrooms, setClassrooms] = useState<ClassroomSummary[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('General');

  // Local editable state
  const [name, setName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [notes, setNotes] = useState('');
  const [school, setSchool] = useState<SchoolInfo>({ name: '', district: '', timezone: '', address: '' });
  const [schedule, setSchedule] = useState<Schedule>({ periods: [], notes: '' });
  const [aiContext, setAiContext] = useState<AiContext>({ teaching_style: '', subjects: [], grade_focus: '', notes: '' });
  const [subjectInput, setSubjectInput] = useState('');

  // ── Load classroom list (when no id) ──
  useEffect(() => {
    if (id || !token) return;
    setPickerLoading(true);
    fetch(`${CORE_API}/api/v1/classrooms`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        const list = json.data?.classrooms || [];
        setClassrooms(list);
        // Auto-navigate to first classroom if only one
        if (list.length === 1) {
          navigate(`/portal/teacher/classroom/${list[0].id}/settings`, { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => { setPickerLoading(false); setLoading(false); });
  }, [id, token, navigate]);

  // ── Load single classroom ──
  useEffect(() => {
    if (!id || !token) return;
    setLoading(true);
    fetch(`${CORE_API}/api/v1/classrooms/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.error) throw new Error(json.error.message || json.error);
        const c = json.data;
        setClassroom(c);
        setName(c.name || '');
        setPublicName(c.public_name || '');
        setGradeLevel(c.grade_level || '');
        setNotes(c.notes || '');
        const cfg = c.public_config || {};
        setSchool({ name: '', district: '', timezone: '', address: '', ...cfg.school });
        setSchedule({ periods: [], notes: '', ...cfg.schedule });
        setAiContext({ teaching_style: '', subjects: [], grade_focus: '', notes: '', ...cfg.ai_context });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, token]);

  // ── Save ──
  const save = useCallback(async () => {
    if (!id || !token || saving) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const body: any = {
        name,
        public_name: publicName || null,
        grade_level: gradeLevel || null,
        notes: notes || null,
        public_config: {
          school,
          schedule,
          ai_context: aiContext,
        },
      };
      const res = await fetch(`${CORE_API}/api/v1/classrooms/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message || json.error || 'Save failed');
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 2000);
    } catch (err: any) {
      setSaveMsg(err.message);
    } finally {
      setSaving(false);
    }
  }, [id, token, saving, name, publicName, gradeLevel, notes, school, schedule, aiContext]);

  // Debounced auto-save
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const triggerAutoSave = useCallback(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 1500);
  }, [save]);

  // ── Render ──

  // ── Classroom picker (no id in URL) ──
  if (!id) {
    if (pickerLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Classroom Settings</h1>
        <p className="text-sm text-slate-500 mb-6">Select a classroom to configure.</p>
        {classrooms.length === 0 ? (
          <p className="text-center py-12 text-slate-400">No classrooms found.</p>
        ) : (
          <div className="grid gap-3">
            {classrooms.map((c) => {
              const cfg = c.public_config || {};
              const hasConfig = !!(cfg.school?.name || cfg.schedule?.periods?.length || cfg.ai_context?.teaching_style);
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/portal/teacher/classroom/${c.id}/settings`)}
                  className="flex items-center justify-between rounded-xl border-2 border-slate-200 bg-white p-4 text-left transition-all hover:border-sky-300 hover:shadow-md"
                >
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.name}</h3>
                    <p className="text-xs text-slate-500">{c.grade_level || 'No grade set'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasConfig ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Configured</span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Not configured</span>
                    )}
                    <span className="text-slate-400">&rarr;</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !classroom) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">{error || 'Classroom not found'}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-sky-600 hover:underline">Go back</button>
      </div>
    );
  }

  const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400';
  const labelCls = 'block text-xs font-medium text-slate-500 mb-1';

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="text-xs text-sky-600 hover:underline mb-1 block">&larr; Back</button>
          <h1 className="text-xl font-bold text-slate-800">Classroom Settings</h1>
          <p className="text-sm text-slate-500">{classroom.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {saveMsg && (
            <span className={`text-xs font-medium ${saveMsg === 'Saved' ? 'text-green-600' : 'text-red-500'}`}>
              {saveMsg}
            </span>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === t ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        {tab === 'General' && (
          <>
            <div>
              <label className={labelCls}>Classroom Name *</label>
              <input className={inputCls} value={name} onChange={(e) => { setName(e.target.value); triggerAutoSave(); }} />
            </div>
            <div>
              <label className={labelCls}>Public Display Name</label>
              <input className={inputCls} value={publicName} onChange={(e) => { setPublicName(e.target.value); triggerAutoSave(); }} placeholder="Shown to parents & sponsors" />
            </div>
            <div>
              <label className={labelCls}>Grade Level</label>
              <input className={inputCls} value={gradeLevel} onChange={(e) => { setGradeLevel(e.target.value); triggerAutoSave(); }} placeholder="e.g. 3rd Grade, K-2, High School" />
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea className={inputCls + ' resize-none'} rows={3} value={notes} onChange={(e) => { setNotes(e.target.value); triggerAutoSave(); }} placeholder="Internal notes about this classroom" />
            </div>
          </>
        )}

        {tab === 'School Info' && (
          <>
            <p className="text-xs text-slate-400">This information is used by AI tools to provide context-aware responses.</p>
            <div>
              <label className={labelCls}>School Name</label>
              <input className={inputCls} value={school.name} onChange={(e) => { setSchool({ ...school, name: e.target.value }); triggerAutoSave(); }} placeholder="Lincoln Elementary" />
            </div>
            <div>
              <label className={labelCls}>District</label>
              <input className={inputCls} value={school.district} onChange={(e) => { setSchool({ ...school, district: e.target.value }); triggerAutoSave(); }} placeholder="Springfield Unified School District" />
            </div>
            <div>
              <label className={labelCls}>Timezone</label>
              <select className={inputCls} value={school.timezone} onChange={(e) => { setSchool({ ...school, timezone: e.target.value }); triggerAutoSave(); }}>
                <option value="">Select timezone...</option>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input className={inputCls} value={school.address} onChange={(e) => { setSchool({ ...school, address: e.target.value }); triggerAutoSave(); }} placeholder="123 Main St, Springfield, IL" />
            </div>
          </>
        )}

        {tab === 'Schedule' && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">Define your daily bell schedule. AI tools use this to reference periods and subjects.</p>
              <button
                type="button"
                onClick={() => {
                  setSchedule((prev) => ({ ...prev, periods: [...prev.periods, { ...EMPTY_PERIOD, name: `Period ${prev.periods.length + 1}` }] }));
                  triggerAutoSave();
                }}
                className="rounded-md bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100"
              >
                + Add Period
              </button>
            </div>

            {schedule.periods.length === 0 && (
              <p className="text-center py-8 text-sm text-slate-400">No periods yet. Click "Add Period" to start building your schedule.</p>
            )}

            {schedule.periods.map((period, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-slate-50 p-3">
                <input
                  className="w-28 rounded border border-slate-200 px-2 py-1.5 text-sm"
                  placeholder="Name"
                  value={period.name}
                  onChange={(e) => {
                    const p = [...schedule.periods];
                    p[i] = { ...p[i], name: e.target.value };
                    setSchedule({ ...schedule, periods: p });
                    triggerAutoSave();
                  }}
                />
                <input
                  type="time"
                  className="rounded border border-slate-200 px-2 py-1.5 text-sm"
                  value={period.start}
                  onChange={(e) => {
                    const p = [...schedule.periods];
                    p[i] = { ...p[i], start: e.target.value };
                    setSchedule({ ...schedule, periods: p });
                    triggerAutoSave();
                  }}
                />
                <span className="text-slate-400 text-xs">to</span>
                <input
                  type="time"
                  className="rounded border border-slate-200 px-2 py-1.5 text-sm"
                  value={period.end}
                  onChange={(e) => {
                    const p = [...schedule.periods];
                    p[i] = { ...p[i], end: e.target.value };
                    setSchedule({ ...schedule, periods: p });
                    triggerAutoSave();
                  }}
                />
                <input
                  className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm"
                  placeholder="Subject"
                  value={period.subject}
                  onChange={(e) => {
                    const p = [...schedule.periods];
                    p[i] = { ...p[i], subject: e.target.value };
                    setSchedule({ ...schedule, periods: p });
                    triggerAutoSave();
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setSchedule((prev) => ({ ...prev, periods: prev.periods.filter((_, j) => j !== i) }));
                    triggerAutoSave();
                  }}
                  className="text-slate-400 hover:text-red-500 text-sm px-1"
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}

            <div>
              <label className={labelCls}>Schedule Notes</label>
              <textarea
                className={inputCls + ' resize-none'}
                rows={2}
                value={schedule.notes}
                onChange={(e) => { setSchedule({ ...schedule, notes: e.target.value }); triggerAutoSave(); }}
                placeholder="e.g. Early release on Wednesdays, lunch at 11:30"
              />
            </div>
          </>
        )}

        {tab === 'AI Context' && (
          <>
            <p className="text-xs text-slate-400">
              Additional context that AI tools will use when generating content for this classroom.
              The more you provide, the better the AI can tailor outputs to your needs.
            </p>
            <div>
              <label className={labelCls}>Teaching Style</label>
              <textarea
                className={inputCls + ' resize-none'}
                rows={3}
                value={aiContext.teaching_style}
                onChange={(e) => { setAiContext({ ...aiContext, teaching_style: e.target.value }); triggerAutoSave(); }}
                placeholder="e.g. Project-based learning, Montessori-influenced, emphasis on collaborative work..."
              />
            </div>
            <div>
              <label className={labelCls}>Grade Focus</label>
              <input
                className={inputCls}
                value={aiContext.grade_focus}
                onChange={(e) => { setAiContext({ ...aiContext, grade_focus: e.target.value }); triggerAutoSave(); }}
                placeholder="e.g. 3rd grade reading level, mixed 4th/5th"
              />
            </div>
            <div>
              <label className={labelCls}>Subjects</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {aiContext.subjects.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-700">
                    {s}
                    <button
                      type="button"
                      onClick={() => {
                        setAiContext((prev) => ({ ...prev, subjects: prev.subjects.filter((_, j) => j !== i) }));
                        triggerAutoSave();
                      }}
                      className="hover:text-red-500"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  className={inputCls}
                  value={subjectInput}
                  onChange={(e) => setSubjectInput(e.target.value)}
                  placeholder="Type a subject and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && subjectInput.trim()) {
                      e.preventDefault();
                      setAiContext((prev) => ({ ...prev, subjects: [...prev.subjects, subjectInput.trim()] }));
                      setSubjectInput('');
                      triggerAutoSave();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (subjectInput.trim()) {
                      setAiContext((prev) => ({ ...prev, subjects: [...prev.subjects, subjectInput.trim()] }));
                      setSubjectInput('');
                      triggerAutoSave();
                    }
                  }}
                  className="rounded-lg bg-slate-100 px-3 text-sm text-slate-600 hover:bg-slate-200"
                >
                  Add
                </button>
              </div>
            </div>
            <div>
              <label className={labelCls}>Additional Notes for AI</label>
              <textarea
                className={inputCls + ' resize-none'}
                rows={3}
                value={aiContext.notes}
                onChange={(e) => { setAiContext({ ...aiContext, notes: e.target.value }); triggerAutoSave(); }}
                placeholder="Anything else AI tools should know: special needs, classroom culture, upcoming events..."
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
